import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";
import { ActivityLog, ActivityCategory, ActivityActionType } from "@/types";
import { format, parseISO, subDays, startOfDay, endOfDay } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Category display info
 */
const CATEGORY_INFO: Record<ActivityCategory, { label: string; color: string; icon: string }> = {
  auth: { label: "Authenticatie", color: "#3B82F6", icon: "log-in-outline" },
  profile: { label: "Profiel", color: "#8B5CF6", icon: "person-outline" },
  admin: { label: "Admin", color: "#EF4444", icon: "shield-outline" },
  weekend: { label: "Weekend", color: "#F59E0B", icon: "calendar-outline" },
  team: { label: "Team", color: "#10B981", icon: "people-outline" },
  score: { label: "Score", color: "#EC4899", icon: "trophy-outline" },
  game: { label: "Spel", color: "#6366F1", icon: "game-controller-outline" },
};

/**
 * Action type display names
 */
const ACTION_LABELS: Partial<Record<ActivityActionType, string>> = {
  "auth.login": "Ingelogd",
  "auth.logout": "Uitgelogd",
  "auth.signup": "Account aangemaakt",
  "auth.password_reset": "Wachtwoord reset",
  "profile.update": "Profiel bijgewerkt",
  "admin.role_assign": "Rol toegekend",
  "admin.role_revoke": "Rol ingetrokken",
  "admin.user_block": "Gebruiker geblokkeerd",
  "admin.user_unblock": "Gebruiker gedeblokkeerd",
  "admin.user_delete": "Gebruiker verwijderd",
  "weekend.create": "Weekend aangemaakt",
  "weekend.update": "Weekend bijgewerkt",
  "weekend.activate": "Weekend geactiveerd",
  "weekend.deactivate": "Weekend gedeactiveerd",
  "weekend.finalize": "Weekend afgerond",
  "weekend.unfinalize": "Weekend heropend",
  "weekend.delete": "Weekend verwijderd",
  "team.create": "Team aangemaakt",
  "team.update": "Team bijgewerkt",
  "team.delete": "Team verwijderd",
  "team.member_add": "Teamlid toegevoegd",
  "team.member_remove": "Teamlid verwijderd",
  "score.award": "Punten toegekend",
  "score.deduct": "Punten afgetrokken",
  "game.create": "Spel aangemaakt",
  "game.start": "Spel gestart",
  "game.end": "Spel beëindigd",
};

/**
 * Date filter options
 */
type DateFilter = "today" | "week" | "month" | "all";

const DATE_FILTER_OPTIONS: { value: DateFilter; label: string }[] = [
  { value: "today", label: "Vandaag" },
  { value: "week", label: "Week" },
  { value: "month", label: "Maand" },
  { value: "all", label: "Alles" },
];

/**
 * Log entry component
 */
interface LogEntryProps {
  log: ActivityLog;
  colors: ReturnType<typeof useThemeColors>;
}

const LogEntry = ({ log, colors }: LogEntryProps) => {
  const categoryInfo = CATEGORY_INFO[log.action_category] || {
    label: log.action_category,
    color: "#6B7280",
    icon: "ellipse-outline",
  };
  const actionLabel = ACTION_LABELS[log.action_type] || log.action_type;
  const userName = log.user?.full_name || "Onbekend";
  const createdAt = parseISO(log.created_at);

  return (
    <View
      className="px-4 py-3"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      <View className="flex-row items-start">
        {/* Category Icon */}
        <View
          className="w-10 h-10 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: `${categoryInfo.color}15` }}
        >
          <Ionicons
            name={categoryInfo.icon as any}
            size={20}
            color={categoryInfo.color}
          />
        </View>

        {/* Content */}
        <View className="flex-1">
          <View className="flex-row items-center flex-wrap">
            <Text className="font-semibold" style={{ color: colors.text }}>
              {actionLabel}
            </Text>
            <View
              className="ml-2 px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${categoryInfo.color}15` }}
            >
              <Text
                className="text-xs font-medium"
                style={{ color: categoryInfo.color }}
              >
                {categoryInfo.label}
              </Text>
            </View>
          </View>

          <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            Door: <Text className="font-medium">{userName}</Text>
          </Text>

          {log.target_name && (
            <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
              {log.target_type === "user" ? "Gebruiker: " : ""}
              {log.target_type === "weekend" ? "Weekend: " : ""}
              <Text className="font-medium">{log.target_name}</Text>
            </Text>
          )}

          {log.metadata?.role ? (
            <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
              Rol: <Text className="font-medium">{String(log.metadata.role)}</Text>
            </Text>
          ) : null}

          <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
            {format(createdAt, "d MMM yyyy 'om' HH:mm", { locale: nl })}
          </Text>
        </View>
      </View>
    </View>
  );
};

/**
 * Filter chip component
 */
interface FilterChipProps {
  label: string;
  isActive: boolean;
  onPress: () => void;
  color?: string;
  colors: ReturnType<typeof useThemeColors>;
}

const FilterChip = ({ label, isActive, onPress, color = "#7C3AED", colors }: FilterChipProps) => (
  <Pressable
    onPress={onPress}
    className="px-3 py-1.5 rounded-full mr-2 mb-2"
    style={{
      backgroundColor: isActive ? `${color}15` : colors.background,
    }}
  >
    <Text
      className="text-sm font-medium"
      style={{ color: isActive ? color : colors.textSecondary }}
    >
      {label}
    </Text>
  </Pressable>
);

/**
 * Activity Logs Screen
 */
export default function LogsScreen() {
  const colors = useThemeColors();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<ActivityCategory | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>("week");
  const [showFilters, setShowFilters] = useState(false);

  /**
   * Fetch logs with filters
   */
  const fetchLogs = useCallback(
    async (reset: boolean = false) => {
      try {
        const currentPage = reset ? 0 : page;
        const from = currentPage * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
          .from("activity_logs")
          .select(
            `
            *,
            user:profiles!activity_logs_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          `
          )
          .order("created_at", { ascending: false })
          .range(from, to);

        // Apply category filter
        if (categoryFilter) {
          query = query.eq("action_category", categoryFilter);
        }

        // Apply date filter
        const now = new Date();
        if (dateFilter === "today") {
          query = query.gte("created_at", startOfDay(now).toISOString());
          query = query.lte("created_at", endOfDay(now).toISOString());
        } else if (dateFilter === "week") {
          query = query.gte("created_at", subDays(now, 7).toISOString());
        } else if (dateFilter === "month") {
          query = query.gte("created_at", subDays(now, 30).toISOString());
        }

        // Apply search filter (on target_name)
        if (searchQuery.trim()) {
          query = query.ilike("target_name", `%${searchQuery.trim()}%`);
        }

        const { data, error } = await query;

        if (error) {
          console.error("Error fetching logs:", error);
          return;
        }

        const typedData = data as unknown as ActivityLog[];

        if (reset) {
          setLogs(typedData);
          setPage(1);
        } else {
          setLogs((prev) => [...prev, ...typedData]);
          setPage((p) => p + 1);
        }

        setHasMore(typedData.length === PAGE_SIZE);
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [categoryFilter, dateFilter, searchQuery, page]
  );

  /**
   * Initial load and filter changes
   */
  useEffect(() => {
    setIsLoading(true);
    setPage(0);
    fetchLogs(true);
  }, [categoryFilter, dateFilter]);

  /**
   * Handle search with debounce
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(true);
      setPage(0);
      fetchLogs(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    setPage(0);
    fetchLogs(true);
  };

  /**
   * Handle load more
   */
  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      fetchLogs(false);
    }
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setCategoryFilter(null);
    setDateFilter("week");
    setSearchQuery("");
  };

  const hasActiveFilters = categoryFilter !== null || dateFilter !== "week" || searchQuery.trim() !== "";

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Search Bar */}
      <View
        className="px-4 py-3"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <View
          className="flex-row items-center rounded-xl px-3 py-2"
          style={{ backgroundColor: colors.background }}
        >
          <Ionicons name="search" size={20} color={colors.textTertiary} />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Zoek op naam..."
            placeholderTextColor={colors.textTertiary}
            className="flex-1 ml-2"
            style={{ color: colors.text }}
          />
          {searchQuery.length > 0 && (
            <Pressable onPress={() => setSearchQuery("")}>
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Date Filter Tabs */}
      <View
        className="px-4 py-2"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {DATE_FILTER_OPTIONS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              isActive={dateFilter === option.value}
              onPress={() => setDateFilter(option.value)}
              color="#DC2626"
              colors={colors}
            />
          ))}
          <View
            className="w-px h-6 mx-2 self-center"
            style={{ backgroundColor: colors.border }}
          />
          <Pressable
            onPress={() => setShowFilters(true)}
            className="flex-row items-center px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.background }}
          >
            <Ionicons name="filter-outline" size={16} color={colors.textSecondary} />
            <Text className="text-sm font-medium ml-1" style={{ color: colors.textSecondary }}>
              Filters {categoryFilter ? "(1)" : ""}
            </Text>
          </Pressable>
        </ScrollView>
      </View>

      {/* Active Filter Indicator */}
      {hasActiveFilters && (
        <View
          className="px-4 py-2 flex-row items-center justify-between"
          style={{ backgroundColor: "#FEF2F2" }}
        >
          <Text className="text-sm" style={{ color: "#B91C1C" }}>
            Filters actief
            {categoryFilter && ` • ${CATEGORY_INFO[categoryFilter].label}`}
          </Text>
          <Pressable onPress={clearFilters}>
            <Text className="text-sm font-medium" style={{ color: "#DC2626" }}>
              Wissen
            </Text>
          </Pressable>
        </View>
      )}

      {/* Logs List */}
      {isLoading && logs.length === 0 ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color="#DC2626" />
          <Text className="mt-2" style={{ color: colors.textSecondary }}>
            Logs laden...
          </Text>
        </View>
      ) : (
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor="#DC2626"
            />
          }
          onMomentumScrollEnd={(e) => {
            const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
            if (contentOffset.y >= contentSize.height - layoutMeasurement.height - 100) {
              handleLoadMore();
            }
          }}
        >
          {logs.length > 0 ? (
            <>
              {logs.map((log) => (
                <LogEntry key={log.id} log={log} colors={colors} />
              ))}
              {hasMore && (
                <Pressable
                  onPress={handleLoadMore}
                  className="py-4 items-center"
                >
                  <Text className="font-medium" style={{ color: "#DC2626" }}>
                    Meer laden...
                  </Text>
                </Pressable>
              )}
            </>
          ) : (
            <View className="flex-1 items-center justify-center py-12">
              <Ionicons name="document-text-outline" size={48} color={colors.textTertiary} />
              <Text className="mt-2" style={{ color: colors.textTertiary }}>
                Geen logs gevonden
              </Text>
              <Text className="text-sm" style={{ color: colors.textTertiary }}>
                Pas de filters aan
              </Text>
            </View>
          )}

          {/* Stats */}
          {logs.length > 0 && (
            <View className="px-4 py-4 items-center">
              <Text className="text-sm" style={{ color: colors.textTertiary }}>
                {logs.length} log{logs.length !== 1 ? "s" : ""} geladen
              </Text>
            </View>
          )}
        </ScrollView>
      )}

      {/* Category Filter Modal */}
      <Modal visible={showFilters} animationType="slide" transparent>
        <View className="flex-1 justify-end bg-black/30">
          <View style={{ backgroundColor: colors.surface }} className="rounded-t-3xl">
            <View
              className="flex-row justify-between items-center px-4 py-4"
              style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
            >
              <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                Categorie Filter
              </Text>
              <Pressable onPress={() => setShowFilters(false)}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </Pressable>
            </View>

            <ScrollView className="max-h-96">
              {/* All categories option */}
              <Pressable
                onPress={() => {
                  setCategoryFilter(null);
                  setShowFilters(false);
                }}
                className="flex-row items-center px-4 py-4"
                style={{
                  backgroundColor: categoryFilter === null ? "#FEF2F2" : colors.surface,
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border,
                }}
              >
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.background }}
                >
                  <Ionicons name="apps-outline" size={20} color={colors.textSecondary} />
                </View>
                <Text className="flex-1 font-medium" style={{ color: colors.text }}>
                  Alle categorieën
                </Text>
                {categoryFilter === null && (
                  <Ionicons name="checkmark" size={20} color="#DC2626" />
                )}
              </Pressable>

              {/* Category options */}
              {(Object.keys(CATEGORY_INFO) as ActivityCategory[]).map((category) => {
                const info = CATEGORY_INFO[category];
                return (
                  <Pressable
                    key={category}
                    onPress={() => {
                      setCategoryFilter(category);
                      setShowFilters(false);
                    }}
                    className="flex-row items-center px-4 py-4"
                    style={{
                      backgroundColor: categoryFilter === category ? "#FEF2F2" : colors.surface,
                      borderBottomWidth: 1,
                      borderBottomColor: colors.border,
                    }}
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: `${info.color}15` }}
                    >
                      <Ionicons name={info.icon as any} size={20} color={info.color} />
                    </View>
                    <Text className="flex-1 font-medium" style={{ color: colors.text }}>
                      {info.label}
                    </Text>
                    {categoryFilter === category && (
                      <Ionicons name="checkmark" size={20} color="#DC2626" />
                    )}
                  </Pressable>
                );
              })}
            </ScrollView>

            <View className="h-8" />
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}
